import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { getYear } from "date-fns";
import { signIn, signOut } from "entities/auth/api";
import { useAuth } from "entities/auth/hooks/useAuth";
import { IAuthError } from "entities/auth/types";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import TextField from "shared/components/Form/TextField";
import PrimaryButton from "shared/components/PrimaryButton";
import useInputState from "shared/hooks/useInputState";
import { COLOR_GREY_12, COLOR_GREY_8 } from "shared/theme/colors";
import {
  StyledAuthPage,
  StyledErrorContainer,
  StyledForm,
  StyledInputsContainer,
  StyledLogoWrapper,
} from "./styled";

const SignIn: FC = () => {
  const navigate = useNavigate();
  const email = useInputState("email", "");
  const password = useInputState("password", "");
  const [submitErrors, setSubmitErrors] = useState<IAuthError[]>([]);

  useAuth();

  const { mutate: onSignIn, isPending: loading } = useMutation({
    mutationFn: () =>
      signIn({
        email: email.input.value,
        password: password.input.value,
      }),
    onError: async (err) => {
      await signOut();

      let errorMessage = err.message;
      if (errorMessage === "User does not exist.") {
        errorMessage = "Incorrect email or password";
      }
      setSubmitErrors([{ message: errorMessage }] as IAuthError[]);
    },
    onSuccess: () => navigate("/", { replace: true }),
  });

  return (
    <StyledAuthPage>
      <StyledLogoWrapper>
        <img src="logo/rollin-logo.svg" alt="rollin-logo" />
      </StyledLogoWrapper>
      <StyledForm>
        <StyledInputsContainer>
          <TextField
            type="email"
            variant="outlined"
            label="Email"
            labelColor={COLOR_GREY_12}
            placeholder="Enter your email"
            {...email.input}
          />
          <Box mt={3}>
            <TextField
              type="password"
              placeholder="Enter your password"
              labelColor={COLOR_GREY_12}
              label="Password"
              variant="outlined"
              InputProps={{
                autoComplete: "confirmed-password",
              }}
              {...password.input}
            />
          </Box>
          {submitErrors?.length > 0 && (
            <StyledErrorContainer>
              {submitErrors.map((error) => (
                <FormHelperText key={error.message} error>
                  {error.message}
                </FormHelperText>
              ))}
            </StyledErrorContainer>
          )}
        </StyledInputsContainer>
        <Box display="flex" justifyContent="flex-end" mt="1.5rem">
          <PrimaryButton
            size="small"
            title="Login"
            rounded={false}
            loading={loading}
            onClick={() => onSignIn()}
          />
        </Box>
      </StyledForm>
      <Typography variant="bodyMRegular" color={COLOR_GREY_8}>
        Copyright &copy; {getYear(new Date())} Rollin
      </Typography>
    </StyledAuthPage>
  );
};

export default SignIn;
